import React, { useState, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Chip,
  Button,
  IconButton,
  useMediaQuery,
} from '@mui/material';
import {
  AutoAwesome,
  Speed,
  Hub,
  Psychology,
  Architecture,
  Group,
  Cable,
  Cloud,
  Share,
  Schedule,
  TuneRounded,
  Refresh,
  ArrowForward,
  ArrowBack,
  IntegrationInstructions,
  Security,
  CloudDone,
} from '@mui/icons-material';
import { useThemeMode } from '../contexts/ThemeContext';
import { useScrollReveal } from '../hooks/useScrollReveal';
import CursorOrbCard from './CursorOrbCard';

function CoreFeatures() {
  const { isLight } = useThemeMode();
  const [showMore, setShowMore] = useState(false);
  const sectionRef = useRef(null);
  const isMobile = useMediaQuery('(max-width:600px)');
  const { ref: revealRef, isVisible, revealStyle, stagger } = useScrollReveal();

  const coreFeatures = [
    {
      title: 'Native Performance Engine',
      description:
        'Execute business logic with native Java performance, delivering speeds equivalent to custom-coded solutions.',
      icon: Speed,
      category: 'Performance',
      color: isLight ? '#6366f1' : '#059669',
      bgColor: isLight
        ? 'rgba(255, 255, 255, 0.6)'
        : 'rgba(5, 150, 105, 0.15)',
    },
    {
      title: 'Visual Integration Middleware',
      description:
        'Connect disparate systems through an intuitive visual workflow designer with advanced logic modeling.',
      icon: Hub,
      category: 'No-Code',
      color: isLight ? '#6366f1' : '#a855f7',
      bgColor: isLight
        ? 'rgba(255, 255, 255, 0.6)'
        : 'rgba(168, 85, 247, 0.15)',
    },
    {
      title: 'AI-Powered Workflow Creation',
      description:
        'Leverage AI to automatically generate, optimize, and suggest workflow improvements based on your requirements.',
      icon: Psychology,
      category: 'AI-Driven',
      color: isLight ? '#6366f1' : '#0891b2',
      bgColor: isLight
        ? 'rgba(255, 255, 255, 0.6)'
        : 'rgba(8, 145, 178, 0.15)',
    },
    {
      title: 'Microservices Orchestration',
      description:
        'Deploy and manage independent microservices with granular control over distribution and scaling.',
      icon: Architecture,
      category: 'Architecture',
      color: isLight ? '#6366f1' : '#d97706',
      bgColor: isLight
        ? 'rgba(255, 255, 255, 0.6)'
        : 'rgba(217, 119, 6, 0.15)',
    },
    {
      title: 'Universal Data Connectors',
      description:
        'Pre-built connectors for databases, web services, message queues, caches, and 200+ enterprise systems. Full ETL, real-time data sync, and API integration included.',
      icon: Cable,
      category: 'Connectivity',
      color: isLight ? '#6366f1' : '#ea580c',
      bgColor: isLight
        ? 'rgba(255, 255, 255, 0.6)'
        : 'rgba(234, 88, 12, 0.15)',
    },
    {
      title: 'Enterprise Team Management',
      description:
        'Role-based access control, team collaboration tools, and enterprise-grade security management.',
      icon: Group,
      category: 'Collaboration',
      color: isLight ? '#6366f1' : '#6366f1',
      bgColor: isLight
        ? 'rgba(255, 255, 255, 0.6)'
        : 'rgba(99, 102, 241, 0.15)',
    },
    {
      title: 'Hybrid Deployment Platform',
      description:
        'Deploy on-premises, in the cloud, or in hybrid configurations with seamless environment management.',
      icon: Cloud,
      category: 'Infrastructure',
      color: isLight ? '#6366f1' : '#0284c7',
      bgColor: isLight
        ? 'rgba(99, 102, 241, 0.08)'
        : 'rgba(2, 132, 199, 0.15)',
    },
    {
      title: 'Cross-Team Workflow Sharing',
      description:
        'Share and collaborate on workflows across teams with version control, permissions, and audit trails.',
      icon: Share,
      category: 'Collaboration',
      color: isLight ? '#6366f1' : '#db2777',
      bgColor: isLight
        ? 'rgba(255, 255, 255, 0.6)'
        : 'rgba(219, 39, 119, 0.15)',
    },
    {
      title: 'Advanced Workflow Scheduling',
      description:
        'Schedule workflows with cron-like precision, dependency chains, and intelligent retry mechanisms.',
      icon: Schedule,
      category: 'Automation',
      color: isLight ? '#6366f1' : '#0d9488',
      bgColor: isLight
        ? 'rgba(99, 102, 241, 0.08)'
        : 'rgba(13, 148, 136, 0.15)',
    },
    {
      title: 'Workflow Performance Tuning',
      description:
        'Monitor and optimize individual workflow performance with detailed analytics and bottleneck detection.',
      icon: TuneRounded,
      category: 'Optimization',
      color: isLight ? '#6366f1' : '#ea580c',
      bgColor: isLight
        ? 'rgba(99, 102, 241, 0.08)'
        : 'rgba(234, 88, 12, 0.15)',
    },
    {
      title: 'Workflow Reusability Framework',
      description:
        'Build reusable workflow templates and component libraries to accelerate development across projects.',
      icon: Refresh,
      category: 'Efficiency',
      color: isLight ? '#6366f1' : '#818cf8',
      bgColor: isLight
        ? 'rgba(99, 102, 241, 0.08)'
        : 'rgba(129, 140, 248, 0.15)',
    },
  ];

  const firstPage = coreFeatures.slice(0, 6);
  const secondPage = coreFeatures.slice(6);

  const handleToggle = () => {
    setShowMore((prev) => {
      const next = !prev;
      // When collapsing back, scroll to section top
      if (!next && sectionRef.current) {
        setTimeout(() => {
          sectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 50);
      }
      return next;
    });
  };

  const renderCard = (feature, index) => (
    <Grid item xs={12} sm={6} lg={4} key={index} sx={{ ...stagger(index % 6, 150) }}>
      <CursorOrbCard color={feature.color} isLight={isLight}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, sm: 3.5 },
          height: '100%',
          background: isLight ? 'rgba(255, 255, 255, 0.4)' : feature.bgColor,
          backdropFilter: 'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
          border: isLight 
            ? '1px solid rgba(255, 255, 255, 0.5)'
            : `1px solid ${feature.color}25`,
          borderRadius: 3,
          boxShadow: isLight 
            ? '0 8px 32px rgba(99, 102, 241, 0.05)'
            : 'none',
          transition: 'all 0.3s ease',
          cursor: 'default',
          '&:hover': {
            transform: 'translateY(-4px)',
            borderColor: isLight
              ? `${feature.color}40`
              : `${feature.color}50`,
            boxShadow: isLight
              ? `0 16px 40px ${feature.color}12`
              : `0 12px 32px ${feature.color}15`,
            '& .feature-icon': {
              animation: 'icon-pop 0.4s ease forwards',
            },
            '& .feature-icon-bg': {
              boxShadow: `0 0 0 4px ${feature.color}12`,
            },
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mb: 2,
          }}
        >
          <Box
            className="feature-icon-bg"
            sx={{
              p: 1.5,
              borderRadius: 2,
              background: `${feature.color}15`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'box-shadow 0.3s ease',
            }}
          >
            {React.createElement(feature.icon, {
              className: 'feature-icon',
              sx: { fontSize: '1.8rem', color: feature.color, transition: 'transform 0.3s ease' },
            })}
          </Box>
          <Chip
            label={feature.category}
            size="small"
            sx={{
              background: feature.color,
              color: 'white',
              fontWeight: 700,
              fontSize: '0.65rem',
              height: 22,
            }}
          />
        </Box>

        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: isLight ? '#0f172a' : '#ffffff',
            mb: 1,
            fontSize: { xs: '1rem', sm: '1.1rem' },
            lineHeight: 1.3,
          }}
        >
          {feature.title}
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: isLight
              ? 'rgba(15, 23, 42, 0.7)'
              : 'rgba(248, 250, 252, 0.7)',
            lineHeight: 1.6,
            fontSize: '0.9rem',
          }}
        >
          {feature.description}
        </Typography>
      </Paper>
      </CursorOrbCard>
    </Grid>
  );

  const primaryColor = isLight ? '#6366f1' : '#10b981';

  return (
    <Box
      component="section"
      id="why-alphanetix"
      ref={sectionRef}
      sx={{
        py: { xs: 6, md: 10 },
        position: 'relative',
        overflowX: 'hidden',
        background: isLight
          ? 'transparent'
          : 'linear-gradient(135deg, #020617 0%, #0f172a 30%, #1e293b 60%, #334155 70%, #0f172a 90%, #020617 100%)',
        backdropFilter: isLight ? 'blur(20px)' : undefined,
        WebkitBackdropFilter: isLight ? 'blur(20px)' : undefined,
      }}
    >
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }} ref={revealRef}>
        {/* Section Header */}
        <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 6 }, ...revealStyle(0) }}>
          <Chip
            icon={<AutoAwesome sx={{ fontSize: '1rem' }} />}
            label="WHY CHOOSE ALPHANETIX"
            sx={{
              mb: 2,
              px: 2,
              background: isLight
                ? 'rgba(255, 255, 255, 0.6)'
                : 'rgba(16, 185, 129, 0.15)',
              border: isLight
                ? '1px solid rgba(255, 255, 255, 0.8)'
                : '1px solid rgba(16, 185, 129, 0.4)',
              borderRadius: '50px',
              color: isLight ? '#1e293b' : '#ffffff',
              fontSize: '0.75rem',
              fontWeight: 700,
              letterSpacing: '1px',
              '& .MuiChip-icon': {
                color: isLight ? '#818cf8' : '#10b981',
              },
              position: 'relative',
              overflow: 'hidden',
              '&::after': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: '-75%',
                width: '50%',
                height: '100%',
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.25), transparent)',
                transform: 'skewX(-20deg)',
                animation: 'shine-sweep 4s ease-in-out infinite',
                animationDelay: '0.5s',
              },
            }}
          />

          <Typography
            variant="h2"
            component="h2"
            sx={{
              fontSize: { xs: '1.8rem', sm: '2.5rem', md: '3.2rem' },
              fontWeight: 900,
              lineHeight: 1.1,
              mb: 1.5,
              color: isLight ? '#0f172a' : '#ffffff',
            }}
          >
            Enterprise Integration &{' '}
            <Box
              component="span"
              sx={{
                background: isLight
                  ? 'linear-gradient(135deg, #4f46e5, #6366f1, #818cf8)'
                  : 'linear-gradient(135deg, #38bdf8, #8b5cf6)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Automation Platform
            </Box>
          </Typography>

          <Typography
            variant="body1"
            sx={{
              fontSize: { xs: '0.95rem', md: '1.1rem' },
              color: isLight
                ? 'rgba(15, 23, 42, 0.65)'
                : 'rgba(248, 250, 252, 0.65)',
              maxWidth: 600,
              mx: 'auto',
            }}
          >
            The world's fastest no-code integration and data pipeline platform,
            engineered for enterprises who demand speed, security, and scalability.
          </Typography>
        </Box>

        {/* Quick-stat bar (from Introduction) */}
        <Grid
          container
          spacing={2}
          sx={{ mb: { xs: 4, md: 6 }, ...revealStyle(1) }}
          justifyContent="center"
        >
          {[
            { icon: <Speed sx={{ fontSize: '1.3rem' }} />, label: 'Native Performance', metric: '10x Faster', color: '#0ea5e9' },
            { icon: <IntegrationInstructions sx={{ fontSize: '1.3rem' }} />, label: 'Zero-Code Platform', metric: 'No Coding', color: '#10b981' },
            { icon: <Security sx={{ fontSize: '1.3rem' }} />, label: 'Enterprise Security', metric: '99.9% Uptime', color: '#a855f7' },
            { icon: <CloudDone sx={{ fontSize: '1.3rem' }} />, label: 'Auto-Scaling', metric: 'Unlimited', color: '#38bdf8' },
            { icon: <Schedule sx={{ fontSize: '1.3rem' }} />, label: 'Response Time', metric: '<100ms', color: '#f472b6' },
          ].map((stat, i) => (
            <Grid item xs={6} sm={4} md key={i}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  textAlign: 'center',
                  borderRadius: 3,
                  background: isLight ? 'rgba(255,255,255,0.65)' : 'rgba(15,23,42,0.6)',
                  border: isLight ? '1px solid rgba(0,0,0,0.06)' : `1px solid ${stat.color}20`,
                  backdropFilter: 'blur(12px)',
                  transition: 'all 0.35s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                  cursor: 'default',
                  '&:hover': {
                    transform: 'translateY(-4px) scale(1.03)',
                    borderColor: isLight ? 'rgba(99, 102, 241, 0.3)' : `${stat.color}50`,
                    boxShadow: isLight
                      ? '0 8px 24px rgba(99, 102, 241, 0.12)'
                      : `0 8px 24px ${stat.color}20`,
                    '& .stat-icon': {
                      transform: 'scale(1.2) rotate(-8deg)',
                    },
                    '& .stat-metric': {
                      letterSpacing: '0.5px',
                    },
                  },
                }}
              >
                <Box
                  className="stat-icon"
                  sx={{
                    color: isLight ? '#6366f1' : stat.color,
                    mb: 0.5,
                    transition: 'transform 0.35s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                  }}
                >
                  {stat.icon}
                </Box>
                <Typography
                  className="stat-metric"
                  variant="body2"
                  sx={{
                    fontWeight: 700,
                    color: isLight ? '#4f46e5' : stat.color,
                    fontSize: '0.95rem',
                    transition: 'letter-spacing 0.3s ease',
                  }}
                >
                  {stat.metric}
                </Typography>
                <Typography variant="caption" sx={{ color: isLight ? 'rgba(15,23,42,0.6)' : 'rgba(248,250,252,0.6)' }}>
                  {stat.label}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* Page indicator & count */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: isLight
                ? 'rgba(15, 23, 42, 0.5)'
                : 'rgba(248, 250, 252, 0.5)',
              fontWeight: 600,
              fontSize: '0.85rem',
            }}
          >
            Showing {showMore ? '7 – 11' : '1 – 6'} of {coreFeatures.length}{' '}
            capabilities
          </Typography>

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {/* Dot indicators */}
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: !showMore ? primaryColor : (isLight ? 'rgba(0,0,0,0.15)' : 'rgba(255,255,255,0.2)'),
                transition: 'background 0.3s',
              }}
            />
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: showMore ? primaryColor : (isLight ? 'rgba(0,0,0,0.15)' : 'rgba(255,255,255,0.2)'),
                transition: 'background 0.3s',
              }}
            />

            <IconButton
              onClick={handleToggle}
              size="small"
              aria-label={showMore ? 'Show first page' : 'Show more features'}
              sx={{
                ml: 1,
                color: primaryColor,
                border: `1px solid ${primaryColor}40`,
                '&:hover': {
                  background: `${primaryColor}15`,
                },
              }}
            >
              {showMore ? <ArrowBack fontSize="small" /> : <ArrowForward fontSize="small" />}
            </IconButton>
          </Box>
        </Box>

        {/* Features Grid — animated page swap */}
        <Box
          sx={{
            position: 'relative',
            /* no overflow:hidden — let cards breathe on hover lift */
          }}
        >
          {/* First page (6 cards) */}
          <Box
            sx={{
              transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
              opacity: showMore ? 0 : 1,
              transform: showMore ? 'translateX(-60px)' : 'translateX(0)',
              position: showMore ? 'absolute' : 'relative',
              width: '100%',
              pointerEvents: showMore ? 'none' : 'auto',
              ...(showMore && { height: 0, overflow: 'hidden' }),
            }}
          >
            <Grid container spacing={3}>
              {firstPage.map((feature, index) => renderCard(feature, index))}
            </Grid>
          </Box>

          {/* Second page (5 cards) */}
          <Box
            sx={{
              transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
              opacity: showMore ? 1 : 0,
              transform: showMore ? 'translateX(0)' : 'translateX(60px)',
              position: showMore ? 'relative' : 'absolute',
              width: '100%',
              pointerEvents: showMore ? 'auto' : 'none',
              ...(!showMore && { height: 0, overflow: 'hidden' }),
            }}
          >
            <Grid container spacing={3}>
              {secondPage.map((feature, index) => renderCard(feature, index + 6))}
            </Grid>
          </Box>
        </Box>

        {/* View More / View Less button */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Button
            variant="outlined"
            onClick={handleToggle}
            endIcon={showMore ? <ArrowBack /> : <ArrowForward />}
            sx={{
              borderColor: `${primaryColor}50`,
              color: primaryColor,
              fontWeight: 700,
              textTransform: 'none',
              borderRadius: 2,
              px: 4,
              py: 1,
              fontSize: '0.95rem',
              transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
              '&:hover': {
                borderColor: primaryColor,
                background: `${primaryColor}08`,
                transform: 'translateY(-2px)',
              },
              '&:active': {
                transform: 'translateY(0) scale(0.96)',
                transition: 'transform 0.1s ease',
              },
            }}
          >
            {showMore
              ? 'Back to Core Features'
              : `View ${secondPage.length} More Capabilities`}
          </Button>
        </Box>
      </Container>
    </Box>
  );
}

export default CoreFeatures;
